<template>
  <div class="completitud-externo">
    <header class="completitud-externo__header">
      <img
        class="completitud-externo__logo"
        src="https://archivos.febos.io/publico/portales/escritoriodigital/ed-gris.png"
        alt="Escritorio Digital"
      />
      <div v-if="ejecucion" class="completitud-externo__info">
        <h4 class="completitud-externo__title">
          Completitud de datos{{ ejecucion.nombre ? `: ${ejecucion.nombre}` : '' }}
        </h4>
        <p class="completitud-externo__subtitle">
          {{ jwtData.name }} · {{ jwtData.email }}
        </p>
      </div>
    </header>

    <b-overlay :show="loading" rounded="sm" class="completitud-externo__body">
      <div v-if="error" class="completitud-externo__error">
        <feather-icon icon="AlertTriangleIcon" size="20" />
        <span>{{ error }}</span>
      </div>

      <div v-else-if="sent" class="completitud-externo__done">
        <feather-icon icon="CheckCircleIcon" size="40" class="completitud-externo__done-icon" />
        <h4>¡Listo!</h4>
        <p>Los datos se enviaron correctamente y el documento continúa su flujo.</p>
      </div>

      <div v-else-if="ready" class="completitud-externo__viewer">
        <PdfFormViewer
          v-model="values"
          :fields="fields"
          :s3url="documentUrl"
          :name="documentName"
          :active="true"
        />
      </div>
    </b-overlay>

    <footer v-if="ready && !error && !sent" class="completitud-externo__footer">
      <span
        class="completitud-externo__hint"
        :class="{ 'is-error': invalidCount > 0 }"
      >
        <template v-if="invalidCount > 0">
          Faltan {{ invalidCount }} campo(s) por completar o con error.
        </template>
        <template v-else>
          Todo listo para enviar.
        </template>
      </span>
      <b-button
        variant="primary"
        :disabled="invalidCount > 0 || sending"
        @click="submit"
      >
        <b-spinner v-if="sending" small class="mr-1" />
        {{ sending ? 'Enviando...' : 'Enviar' }}
      </b-button>
    </footer>
  </div>
</template>

<script>

import PdfFormViewer from '@/febos/global/pdf/formBuilder/components/PdfFormViewer';
import { getExternalExecution, postExternalCompletion } from '@/febos/servicios/api/aprobaciones.api';
import { validateFieldValue, valueKeyOf } from '@/febos/global/pdf/formBuilder/model/fieldModel';
import { decodeJwt } from '@/febos/global/utils/jwt';

/**
 * CompletitudExterno — vista pública (sin login) a la que llega el responsable
 * externo de un paso de "Completitud de datos" desde el enlace del correo.
 *
 * Decodifica el JWT del enlace, consulta la ejecución externa (endpoint público
 * autenticado por el token) y monta el PdfFormViewer en modo completar con el
 * PDF y los campos definidos en el builder. Al enviar, valida y manda los
 * valores al backend, que los estampa en el PDF y avanza el paso.
 */
export default {
  name: 'CompletitudExterno',
  components: { PdfFormViewer },
  data() {
    return {
      loading: true,
      error: '',
      jwtData: {},
      ejecucion: null,
      fields: [],
      documentUrl: undefined,
      documentName: 'documento.pdf',
      values: {},
      sending: false,
      sent: false,
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    ready() {
      return !this.loading && !!this.documentUrl && !!this.ejecucion;
    },
    // Campos requeridos vacíos o con formato inválido (misma regla que el visor).
    invalidCount() {
      return this.fields
        .filter((f) => !validateFieldValue(f, this.values[valueKeyOf(f)]).valid)
        .length;
    },
  },
  methods: {
    parseFields(configuration) {
      if (!configuration) {
        return [];
      }
      try {
        const parsed = typeof configuration === 'string'
          ? JSON.parse(configuration)
          : configuration;
        return Array.isArray(parsed.campos) ? parsed.campos : [];
      } catch (e) {
        console.error('No se pudo parsear la configuración de campos', e);
        return [];
      }
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        this.jwtData = decodeJwt(this.token);
        const { data } = await getExternalExecution(this.token);

        this.ejecucion = data.ejecucion;
        const pasoEjecucion = data.pasoEjecucion || {};
        this.fields = this.parseFields(pasoEjecucion.configuration);

        const documentos = data.documentos || [];
        if (documentos.length) {
          this.documentUrl = documentos[0].adjuntoUrl;
          this.documentName = documentos[0].objetoNombre || 'documento.pdf';
        }

        if (!this.documentUrl) {
          this.error = 'No se encontró el documento asociado a este flujo.';
        } else if (!this.fields.length) {
          this.error = 'Este paso no tiene campos definidos para completar.';
        }
      } catch (e) {
        console.error('Error al cargar la ejecución externa', e);
        this.error = 'No se pudo cargar el documento. El enlace puede haber expirado '
          + 'o el flujo ya no está disponible.';
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (this.invalidCount > 0 || this.sending) {
        return;
      }
      this.sending = true;
      try {
        // El backend es la fuente de verdad del configuration (campos/coordenadas);
        // enviamos el valor por id de campo. Campos vinculados (misma linkKey)
        // comparten valor, así que cada caja (id) recibe el mismo texto a estampar.
        const valores = this.fields.map((f) => {
          const v = this.values[valueKeyOf(f)];
          return { id: f.id, valor: v != null ? String(v) : '' };
        });
        await postExternalCompletion(this.token, valores);
        this.sent = true;
      } catch (e) {
        console.error('Error al enviar la completitud', e);
        this.$bvToast.toast(
          'No se pudo enviar. Revisa los datos e inténtalo de nuevo.',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 4000,
          },
        );
      } finally {
        this.sending = false;
      }
    },
  },
  created() {
    if (!this.token) {
      this.loading = false;
      this.error = 'Enlace inválido: falta el token de acceso.';
      return;
    }
    this.load();
  },
};

</script>

<style lang="scss" scoped>

.completitud-externo {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: #f5f5f8;
}

.completitud-externo__header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  background: #ffffff;
  border-bottom: 1px solid #e3e3ef;
}

.completitud-externo__logo {
  height: 36px;
}

.completitud-externo__title {
  margin: 0;
  font-size: 16px;
  color: #4a4a4a;
}

.completitud-externo__subtitle {
  margin: 0;
  font-size: 12px;
  color: #8a8aa0;
}

.completitud-externo__body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.completitud-externo__viewer {
  flex: 1;
  min-height: 0;
  display: flex;
}

.completitud-externo__error {
  margin: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #b11e0b;
  font-size: 14px;
}

.completitud-externo__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #ffffff;
  border-top: 1px solid #e3e3ef;
}

.completitud-externo__hint {
  font-size: 12px;
  color: #8a8aa0;
}

.completitud-externo__hint.is-error {
  color: #ea5455;
  font-weight: 600;
}

.completitud-externo__done {
  margin: auto;
  text-align: center;
  color: #4a4a4a;
}

.completitud-externo__done-icon {
  color: #28c76f;
  margin-bottom: 8px;
}

</style>
